import { ReactElement, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { isRenderingRepeatedFieldGroup, RenderingFieldGroup } from '@zinnia/form-engine-sdk';
import { ImageTypes } from '@breathelife/ui-components';

import { FieldGroupCardModal } from '../../../../../Components/AssistedApplication/Modals/FieldGroupCard/FieldGroupCardModal';
import { Icon } from '../../../../../Components/Icons';
import { buildAttributeIdWithIndex } from '../../../../../Helpers/assistedApplication/attributeIds';
import { useModalState } from '../../../../../Hooks';
import { useAssistedApplicationContext } from '../../../../../Hooks/useAssistedApplicationContext';
import { FieldGroupCardPreview } from './FieldGroupCardPreview';
import { AddButton, DeleteButton } from './Styles';
import { useQuestionnaireEngine } from '../../../../../Hooks/useQuestionnaireEngine';

type FieldGroupCardContainerProps = {
  fieldGroup: RenderingFieldGroup;
  focusedIncompleteFieldId: string | undefined;
};

export function FieldGroupCardContainer(props: FieldGroupCardContainerProps): ReactElement {
  const { fieldGroup, focusedIncompleteFieldId } = props;
  const { t } = useTranslation();
  const [isModalOpen, onOpenModal, onCloseModal] = useModalState();
  const { onAnswerChange } = useAssistedApplicationContext();
  const { questionnaireEngine } = useQuestionnaireEngine();

  const hasFocusedIncompleteField = fieldGroup.fields.some((field) => field.blueprintId === focusedIncompleteFieldId);

  return (
    <Fragment>
      <FieldGroupCardPreview fieldGroup={fieldGroup} onEdit={onOpenModal} boldedBorder={hasFocusedIncompleteField} />

      {isRenderingRepeatedFieldGroup(fieldGroup) && fieldGroup.showAddQuestionButton && (
        <AddButton
          variant='text'
          color='primary'
          startIcon={
            <Icon
              name='add'
              variant='primary'
              imageType={ImageTypes.decorative}
              id={buildAttributeIdWithIndex(fieldGroup.addQuestionButtonText, fieldGroup.scope.repetitionIndex + 1)}
            />
          }
          onClick={() => {
            onAnswerChange([
              {
                tag: 'blueprintId',
                blueprintId: fieldGroup.blueprintId,
                nodeId: fieldGroup.nodeId,
                value: {},
                effects: fieldGroup.effects,
                blueprintIdScope: {
                  ...fieldGroup.scope.repeatedInstanceIdentifierContext.byBlueprintId,
                  [fieldGroup.blueprintId]: fieldGroup.scope.repetitionIndex + 1,
                },
                nodeIdScope: {
                  ...fieldGroup.scope.repeatedInstanceIdentifierContext.byNodeId,
                  [fieldGroup.nodeId]: fieldGroup.scope.repetitionIndex + 1,
                },
              },
            ]);
          }}
        >
          {fieldGroup.addQuestionButtonText}
        </AddButton>
      )}

      <FieldGroupCardModal
        isOpen={isModalOpen}
        onCloseModal={onCloseModal}
        secondaryAction={
          isRenderingRepeatedFieldGroup(fieldGroup) && fieldGroup.showRemoveQuestionButton ? (
            <DeleteButton
              variant='text'
              startIcon={
                <Icon
                  name='delete'
                  variant='red'
                  imageType={ImageTypes.decorative}
                  id={buildAttributeIdWithIndex(fieldGroup.removeQuestionButtonText, fieldGroup.scope.repetitionIndex)}
                />
              }
              onClick={() => {
                onCloseModal();
                onAnswerChange([
                  {
                    tag: 'blueprintId',
                    blueprintId: fieldGroup.blueprintId,
                    nodeId: fieldGroup.nodeId,
                    value: undefined,
                    effects: fieldGroup.effects,
                    blueprintIdScope: fieldGroup.scope.repeatedInstanceIdentifierContext.byBlueprintId,
                    nodeIdScope: fieldGroup.scope.repeatedInstanceIdentifierContext.byNodeId,
                  },
                ]);
              }}
            >
              {fieldGroup.removeQuestionButtonText}
            </DeleteButton>
          ) : (
            <DeleteButton
              variant='text'
              startIcon={<Icon imageType={ImageTypes.decorative} name='delete' variant='red' id='clear' />}
              onClick={() => {
                onCloseModal();
                questionnaireEngine.onBulkAnswerClear(
                  fieldGroup.fields,
                  fieldGroup.scope?.repeatedInstanceIdentifierContext.byBlueprintId || {},
                  fieldGroup.scope?.repeatedInstanceIdentifierContext.byNodeId || {},
                );
              }}
            >
              {t('cta.clear')}
            </DeleteButton>
          )
        }
        fieldGroup={fieldGroup}
      />
    </Fragment>
  );
}
